import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
import { environment } from 'src/environments/environment'
import { ElementStatus } from '../platform/busines-data/SectionElements/InspectionElements/elementStatus'
import { IFilter } from './filter.component'
import { OfflineService } from '@phdsoft/common'

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  constructor(
    private http: HttpClient,
    private readonly offlineService: OfflineService
  ) { }

  getFilter(id: string) {
    return this.http.get(environment.API_INSPEC + `Filter/${id}`)
  }

  getFiltersByType(assetId: String, type: String, regionIds: String[]) {
    return this.http.post<any>(environment.API_INSPEC + `Filter/GetFiltersByType/${assetId}/${type}`, regionIds)
  }

  getFiltersByRegionIds(assetId: string, regionIds: string[]) {
    const preventCache = (new Date()).getTime();
    if (window.navigator.onLine) {
      return this.http.post<any[]>(environment.API_INSPEC + `Filter/GetFiltersByRegions/${assetId}?cache_bump=${preventCache}`, regionIds)
    }
    else {
      return new Observable<any[]>(observer => {
        this.getFiltersOffline(assetId, regionIds).then(filters => {
          observer.next(filters);
          observer.complete();
        }).catch(error => {
          observer.error(error);
        });
      });
    }
  }

  private async getFiltersOffline(assetId: string, regionIds: string[]): Promise<IFilter[]> {
    try {
      let allFilters: IFilter[] = [];
      
      for (const regionId of regionIds) {
        const filtersData = await this.offlineService.getItem(`filters_${assetId}_${regionId}`);
        if (filtersData && filtersData.json) {
          const regionFilters = Array.isArray(filtersData.json) ? filtersData.json : [filtersData.json];
          allFilters = allFilters.concat(regionFilters);
        }
      }
      
      return allFilters;
    } catch (error) {
      console.error('Error getting filters offline:', error);
      return [];
    }
  }

  GetInspectionCountByRegionIdAndDateAndInspectionType(assetId: String, regionId: String, startDate: string, endDate: string, inspectionTypeIds: any[]) {
    const preventCache = (new Date()).getTime();
    return this.http.post<number>(environment.API_INSPEC + `Inspection/GetInspectionCountByRegionIdAndDateAndInspectionType?assetId=${assetId}&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}&cache_bump=${preventCache}`, inspectionTypeIds)
  }

  GetInspectionCountByCluster(assetId: String, regionId: String, startDate: Date, endDate: Date, inspectionTypeIds: any[]) {
    const preventCache = (new Date()).getTime();
    return this.http.post<number>(environment.API_INSPEC + `Inspection/GetInspectionCountByCluster?assetId=${assetId}&regionId=${regionId}&startDate=${startDate.toJSON()}&endDate=${endDate.toJSON()}&cache_bump=${preventCache}`, inspectionTypeIds)
  }

  GetInspectionCountBySectionIdAndDateAndInspectionType(assetId: String, regionId: string, sectionId: String, inspectionTypeIds: any[]) {
    const preventCache = (new Date()).getTime();
    var result = this.http.post<number>(environment.API_INSPEC + `Inspection/GetInspectionCountBySectionIdAndDateAndInspectionType?assetId=${assetId}&regionId=${regionId}&sectionId=${sectionId}&cache_bump=${preventCache}`, inspectionTypeIds)
    return result
  }

  GetInspectionCountByRegionIdAndDateCluster(assetId: String, regionId: String, startDate: string, endDate: string) {
    const preventCache = (new Date()).getTime();
    return this.http.get<Number>(environment.API_INSPEC + `Inspection/GetInspectionCountByRegionIdAndDateCluster?assetId=${assetId}&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}&cache_bump=${preventCache}`)
  }

  GetCountByInspectionStatus(assetId: String, regionId: string, startDate: string, endDate: string, status: ElementStatus, inspectionTypeIds: string[]) {
    const preventCache = (new Date()).getTime();
    return this.http.get<number>(environment.API_INSPEC + `Inspection/GetCountByInspectionStatus?assetId=${assetId}&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}&status=${status}&inspectionTypeIds=${inspectionTypeIds.join(',')}&cache_bump=${preventCache}`)
  }

  GetCountByActionPlanIdAndStatus(assetId: String, regionId: string, status: ElementStatus, actionPlanId: string) {
    const preventCache = (new Date()).getTime();
    return this.http.get<number>(environment.API_INSPEC + `Inspection/GetCountByActionPlanIdAndStatus?assetId=${assetId}&regionId=${regionId}&status=${status}&actionPlanId=${actionPlanId}&cache_bump=${preventCache}`)
  }

  GetCountByActionPlanId(assetId: String, regionId: string, actionPlanId: string) {
    const preventCache = (new Date()).getTime();
    return this.http.get<number>(environment.API_INSPEC + `Inspection/GetCountByActionPlanId?assetId=${assetId}&regionId=${regionId}&actionPlanId=${actionPlanId}&cache_bump=${preventCache}`)
  }

  /**
   * Counts by region membership (the element's region list), so filters and clusters
   * keep including elements from adjacent regions. Plan counters use
   * {@link GetCountByInspectionStatusForPlan} instead.
   */
  GetCountByInspectionStatusInsertedRegion(assetId: string, regionId: string, startDate: string, endDate: string, status: ElementStatus, inspectionTypeIds: string[], criticalAreaIds?: string[], isActionPlan?: boolean) {
    const params = this.countByInspectionStatusParams(assetId, regionId, startDate, endDate, status, inspectionTypeIds, criticalAreaIds, isActionPlan);
    return this.http.get<number>(`${environment.API_INSPEC}Inspection/GetCountByInspectionStatusInsertedRegion?${params.toString()}`);
  }

  /**
   * Plan counters (Schedule): counts by the element's inserted region, so an element
   * shared with an adjacent region only counts toward the plan of the region it was
   * inserted in. Filters/clusters keep {@link GetCountByInspectionStatusInsertedRegion}
   * (membership). Same query string and numeric response as that route.
   */
  GetCountByInspectionStatusForPlan(assetId: string, regionId: string, startDate: string, endDate: string, status: ElementStatus, inspectionTypeIds: string[], criticalAreaIds?: string[], isActionPlan?: boolean) {
    const params = this.countByInspectionStatusParams(assetId, regionId, startDate, endDate, status, inspectionTypeIds, criticalAreaIds, isActionPlan);
    return this.http.get<number>(`${environment.API_INSPEC}Inspection/GetCountByInspectionStatusForPlan?${params.toString()}`);
  }

  private countByInspectionStatusParams(assetId: string, regionId: string, startDate: string, endDate: string, status: ElementStatus, inspectionTypeIds: string[], criticalAreaIds?: string[], isActionPlan?: boolean): URLSearchParams {
    const preventCache = new Date().getTime();
    const params = new URLSearchParams({
      assetId,
      regionId,
      startDate,
      endDate,
      status: status.toString(),
      inspectionTypeIds: inspectionTypeIds.join(','),
      cache_bump: preventCache.toString()
    });

    if (isActionPlan !== undefined) {
      params.append('isActionPlan', isActionPlan.toString());
    }

    if (criticalAreaIds && criticalAreaIds.length > 0) {
      params.append('criticalAreaIds', criticalAreaIds.join(','));
    }

    return params;
  }

  GetLockedOrUnlockedElementsCount(assetId: String, regionId: string, startDate: string, endDate: string, isLocked: boolean, inspectionTypeIds: string[]) {
    const preventCache = (new Date()).getTime();
    return this.http.get<number>(environment.API_INSPEC + `Inspection/GetLockedOrUnlockedElementsCount?assetId=${assetId}&regionId=${regionId}&startDate=${startDate}&endDate=${endDate}&isLocked=${isLocked}&inspectionTypeIds=${inspectionTypeIds.join(',')}&cache_bump=${preventCache}`)
  }

  GetInspectionCountTotalByInsertedRegion(assetId: string, regionId: string, startDate: string, endDate: string, inspectionTypeIds: string[], criticalAreaIds?: string[], isActionPlan?: boolean) {
    const preventCache = new Date().getTime();

    const params = new URLSearchParams({
      assetId,
      regionId,
      startDate,
      endDate,
      inspectionTypeIds: inspectionTypeIds.join(','),
      cache_bump: preventCache.toString()
    });

    if (criticalAreaIds && criticalAreaIds.length > 0) {
      params.append('criticalAreaIds', criticalAreaIds.join(','));
    }

    if (isActionPlan !== undefined) {
      params.append('isActionPlan', isActionPlan.toString());
    }

    const url = `${environment.API_INSPEC}Inspection/GetInspectionCountTotalByInsertedRegion`;
    
    return this.http.get<number>(`${url}?${params.toString()}`);
  }

  getAllFilters(assetId: String) {
    return this.http.get<IFilter[]>(environment.API_INSPEC + `Filter/GetAllFilters/${assetId}`)
  }

  putFilter(filter: IFilter) {
    return this.http.put<any>(environment.API_INSPEC + `Filter`, filter)
  }

  postFilter(filter: IFilter) {
    return this.http.post<any>(environment.API_INSPEC + `Filter`, filter)
  }

  deleteFilter(id: String, assetId: String) {
    return this.http.delete<boolean>(environment.API_INSPEC + `Filter/${id}?assetId=${assetId}`)
  }
  joinFlangesRetarget(assetId: string, regionId: string, srcTargetId: string, destTargetId) {
    var result = this.http.post<number>(environment.API_INSPEC + 
      `Inspection/JoinFlangesRetarget?assetId=${assetId}&regionId=${regionId}&srcTargetId=${srcTargetId}&destTargetId=${destTargetId}`,null)
    return result
  }
}
